import React, { useEffect, useState } from 'react';
import { Typography, Modal, Form, Input, Select } from 'antd';
import { message } from '../../../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from '../../../contextProviders/UserProfileProvider';
import { useAuth } from '../../../contextProviders/AuthProvider';
import { updateMyUserProfile } from '../../../services/UserProfileService';
import { getCountries, ICountryResponse } from '../../../services/LocaleService';
import AvatarUpload from '../../../components/AvatarUpload';
import PhoneNumberInput from '../../../components/PhoneNumberInput';
import SettingsRow from '../../../components/Settings/SettingsRow';
import SettingsCard from '../../../components/Settings/SettingsCard';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';

const { Title } = Typography;
const { Option } = Select;

type EditField = 'displayName' | 'phone' | 'country' | null;

/** "Configuración > Información personal" -- one independent Editar per row, same row+link pattern as the rest of the redesigned Settings page. */
const PersonalInfoSection: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { userName } = useAuth();
  const { userProfile, refresh } = useUserProfile();
  const [countries, setCountries] = useState<ICountryResponse[]>([]);
  const [editField, setEditField] = useState<EditField>(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    getCountries().then(setCountries).catch(() => undefined);
  }, []);

  const openEdit = (field: EditField) => {
    form.setFieldsValue({
      displayName: userProfile?.displayName,
      phone: userProfile?.phone,
      alpha2Code: userProfile?.countryAlpha2Code,
    });
    setEditField(field);
  };

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      if (editField === 'displayName') {
        await updateMyUserProfile({ displayName: values.displayName });
      } else if (editField === 'phone') {
        await updateMyUserProfile({ phone: values.phone });
      } else if (editField === 'country') {
        const country = countries.find((c) => c.alpha2Code === values.alpha2Code);
        await updateMyUserProfile({ country: country?.name, countryAlpha2Code: values.alpha2Code });
      }
      await refresh();
      message.success(translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
      setEditField(null);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = async (url: string) => {
    setSaving(true);
    try {
      await updateMyUserProfile({ photoUrl: url });
      await refresh();
      message.success(translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Redundant on mobile -- the list->detail header (Settings.tsx) already shows this section's name. */}
      {!isMobile && (
        <Title level={4} style={{ marginBottom: 20 }}>
          {translate(t, TRANSLATIONS.SETTINGS_NAV_PERSONAL)}
        </Title>
      )}

      <SettingsCard noMarginBottom>
        <SettingsRow
          label={translate(t, TRANSLATIONS.SETTINGS_PERSONAL_DISPLAY_NAME_LABEL)}
          value={userProfile?.displayName || '—'}
          actionLabel={translate(t, TRANSLATIONS.SETTINGS_EDIT_LINK)}
          onAction={() => openEdit('displayName')}
        />
        <SettingsRow
          label={translate(t, TRANSLATIONS.SETTINGS_PERSONAL_EMAIL_LABEL)}
          value={userName || '—'}
          actionLabel={translate(t, TRANSLATIONS.SETTINGS_EDIT_LINK)}
          onAction={() => message.info(translate(t, TRANSLATIONS.SETTINGS_PERSONAL_EMAIL_EDIT_HINT))}
        />
        <SettingsRow
          label={translate(t, TRANSLATIONS.SETTINGS_PERSONAL_PHONE_LABEL)}
          value={userProfile?.phone || '—'}
          actionLabel={translate(t, TRANSLATIONS.SETTINGS_EDIT_LINK)}
          onAction={() => openEdit('phone')}
        />
        <SettingsRow
          label={translate(t, TRANSLATIONS.SETTINGS_PERSONAL_COUNTRY_LABEL)}
          value={userProfile?.country || '—'}
          actionLabel={translate(t, TRANSLATIONS.SETTINGS_EDIT_LINK)}
          onAction={() => openEdit('country')}
        />
        <SettingsRow
          label={translate(t, TRANSLATIONS.SETTINGS_PERSONAL_PHOTO_LABEL)}
          value={translate(t, TRANSLATIONS.SETTINGS_PERSONAL_PHOTO_HINT)}
          actionLabel={translate(t, TRANSLATIONS.SETTINGS_CHANGE_LINK)}
          onAction={() => setPhotoModalOpen(true)}
        />
      </SettingsCard>

      <Modal
        title={translate(t, TRANSLATIONS.SETTINGS_EDIT_LINK)}
        open={editField != null}
        onCancel={() => setEditField(null)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okButtonProps={{ shape: 'round' }}
        cancelButtonProps={{ shape: 'round' }}
        styles={{ content: { borderRadius: 20 } }}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          {editField === 'displayName' && (
            <Form.Item name="displayName" label={translate(t, TRANSLATIONS.SETTINGS_PERSONAL_DISPLAY_NAME_LABEL)}>
              <Input disabled={saving} />
            </Form.Item>
          )}
          {editField === 'phone' && (
            <Form.Item name="phone" label={translate(t, TRANSLATIONS.SETTINGS_PERSONAL_PHONE_LABEL)}>
              <PhoneNumberInput disabled={saving} />
            </Form.Item>
          )}
          {editField === 'country' && (
            <Form.Item name="alpha2Code" label={translate(t, TRANSLATIONS.SETTINGS_PERSONAL_COUNTRY_LABEL)}>
              <Select
                showSearch
                disabled={saving}
                filterOption={(input, option: any) => option?.children?.toLowerCase().includes(input.toLowerCase())}
              >
                {countries.map((country) => (
                  <Option key={country.alpha2Code} value={country.alpha2Code}>
                    {country.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={translate(t, TRANSLATIONS.SETTINGS_PERSONAL_PHOTO_LABEL)}
        open={photoModalOpen}
        onCancel={() => setPhotoModalOpen(false)}
        footer={null}
        styles={{ content: { borderRadius: 20 } }}
      >
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <AvatarUpload value={userProfile?.photoUrl} onChange={handlePhotoChange} disabled={saving} />
        </div>
      </Modal>
    </div>
  );
};

export default PersonalInfoSection;
